import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight, MapPin, Clock8 } from "lucide-react";

export default function CustomBooking() {
  const nav = useNavigate();
  const [trip, setTrip] = useState("one_way");
  const [f, setF] = useState({
    customer_name:"", customer_mobile:"", customer_whatsapp:"", customer_email:"",
    from_place:"", to_place:"", pickup_location:"", drop_location:"",
    travel_date: new Date().toISOString().slice(0,10), return_date:"", departure_time:"",
    passengers:2, ac:true, vehicle_type_pref:"Tempo Traveller", notes:"",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = {
        booking_type: trip,
        customer_name: f.customer_name, customer_mobile: f.customer_mobile,
        customer_whatsapp: f.customer_whatsapp || f.customer_mobile,
        customer_email: f.customer_email,
        from_place: f.from_place, to_place: f.to_place,
        pickup_location: f.pickup_location, drop_location: f.drop_location,
        travel_date: f.travel_date, return_date: trip==="round_trip"? f.return_date : null,
        departure_time: f.departure_time, passengers: parseInt(f.passengers)||1,
        ac: !!f.ac, vehicle_type_pref: f.vehicle_type_pref, notes: f.notes,
        source: "custom_booking",
      };
      const r = await api.post("/leads", payload);
      setDone(r.data);
      toast.success(`Enquiry saved! ID: ${r.data.booking_id} — team will send a quote soon.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit"); }
    setLoading(false);
  };

  if (done) return (
    <PublicLayout>
      <section className="max-w-2xl mx-auto px-4 pt-10 pb-16">
        <div className="p-8 rounded-2xl bg-white border border-emerald-200 shadow-sm text-center" data-testid="custom-booking-done">
          <div className="text-emerald-700 text-xs uppercase tracking-widest font-bold">Enquiry Received</div>
          <h1 className="text-3xl font-display font-black tracking-tight mt-2">Thank you, {done.customer_name}!</h1>
          <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <div className="text-xs text-slate-500">Enquiry ID</div>
            <div className="font-display font-black text-2xl text-[#1e1b4b]" data-testid="lead-booking-id">{done.booking_id}</div>
          </div>
          <p className="text-slate-600 mt-4 text-sm">Our team will WhatsApp / call you shortly with a personalised quote. You can also directly call +91 98902 01047.</p>
          <div className="mt-2 p-3 rounded-xl bg-amber-50 border-l-4 border-amber-500 text-xs text-amber-900 text-left flex items-start gap-2">
            <Clock8 className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span><b>Tip:</b> Once confirmed, please arrive 10–15 min early at your pickup point.</span>
          </div>
          <Button onClick={()=>nav("/")} className="mt-6 bg-[#ea580c] hover:bg-orange-700">Back to Home</Button>
        </div>
      </section>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-4 pt-10 pb-16">
        <div className="text-xs uppercase tracking-widest text-[var(--brand-orange)] font-semibold">Custom Booking · New Lead</div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-black tracking-tight">Pick your own places</h1>
        <p className="text-slate-600 mt-3">Tell us where you want to go — one-way or round trip. Our team will send a personalised quote by WhatsApp.</p>

        <form onSubmit={submit} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="custom-booking-form">
          {/* Trip type */}
          <div className="flex gap-2" data-testid="trip-type-toggle">
            <button type="button" onClick={()=>setTrip("one_way")} className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${trip==="one_way"?"bg-[#1e1b4b] text-white border-[#1e1b4b]":"bg-white text-slate-700 border-slate-300"}`} data-testid="trip-oneway">One Way</button>
            <button type="button" onClick={()=>setTrip("round_trip")} className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${trip==="round_trip"?"bg-[#1e1b4b] text-white border-[#1e1b4b]":"bg-white text-slate-700 border-slate-300"}`} data-testid="trip-round">Round Trip</button>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Full Name *</Label><Input required value={f.customer_name} onChange={e=>setF({...f,customer_name:e.target.value})} data-testid="cb-name" /></div>
            <div><Label>Mobile *</Label><Input required value={f.customer_mobile} onChange={e=>setF({...f,customer_mobile:e.target.value})} data-testid="cb-mobile" /></div>
            <div><Label>WhatsApp</Label><Input value={f.customer_whatsapp} placeholder="Same as mobile" onChange={e=>setF({...f,customer_whatsapp:e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={f.customer_email} onChange={e=>setF({...f,customer_email:e.target.value})} /></div>
            <div><Label>From *</Label><Input required value={f.from_place} onChange={e=>setF({...f,from_place:e.target.value})} placeholder="Your place" data-testid="cb-from" /></div>
            <div><Label>To *</Label><Input required value={f.to_place} onChange={e=>setF({...f,to_place:e.target.value})} placeholder="Destination" data-testid="cb-to" /></div>
            <div><Label>Travel Date *</Label><Input required type="date" value={f.travel_date} onChange={e=>setF({...f,travel_date:e.target.value})} data-testid="cb-date" /></div>
            {trip==="round_trip" && <div><Label>Return Date *</Label><Input required type="date" value={f.return_date} onChange={e=>setF({...f,return_date:e.target.value})} data-testid="cb-return" /></div>}
            <div><Label>Pickup Time</Label><Input type="time" value={f.departure_time} onChange={e=>setF({...f,departure_time:e.target.value})} /></div>
            <div><Label>Passengers *</Label><Input required type="number" min="1" value={f.passengers} onChange={e=>setF({...f,passengers:e.target.value})} data-testid="cb-pax" /></div>
            <div>
              <Label>Vehicle Preference</Label>
              <Select value={f.vehicle_type_pref} onValueChange={v=>setF({...f,vehicle_type_pref:v})}>
                <SelectTrigger data-testid="cb-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ertiga">Ertiga (6-seat)</SelectItem>
                  <SelectItem value="Innova">Innova (7-seat)</SelectItem>
                  <SelectItem value="Tempo Traveller">Tempo Traveller (20-seat)</SelectItem>
                  <SelectItem value="Traveller">Traveller (17-seat)</SelectItem>
                  <SelectItem value="Mini Bus">Mini Bus (25-seat)</SelectItem>
                  <SelectItem value="Bus">Bus (30-seat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>AC / Non-AC</Label>
              <Select value={String(f.ac)} onValueChange={v=>setF({...f,ac:v==="true"})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">AC</SelectItem><SelectItem value="false">Non-AC</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label><MapPin className="w-3 h-3 inline text-[var(--brand-orange)]" /> Pickup Location</Label><Input value={f.pickup_location} onChange={e=>setF({...f,pickup_location:e.target.value})} placeholder="Address / landmark" /></div>
            <div className="md:col-span-2"><Label><MapPin className="w-3 h-3 inline text-[var(--brand-orange)]" /> Drop Location</Label><Input value={f.drop_location} onChange={e=>setF({...f,drop_location:e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Notes / Special Requests</Label><Textarea value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} placeholder="Any special requirement?" /></div>
          </div>
          <Button disabled={loading} type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="cb-submit">{loading?"Sending…":"Send Enquiry — Get Quote"} <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </form>
      </section>
    </PublicLayout>
  );
}
